import { InfoIcon } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/chariot-ui';
import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';
import { Tooltip } from '@/components/Tooltip';
import { useMy } from '@/hooks';
import {
  ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
  useGetProfilePictureUrl,
} from '@/hooks/profilePicture';
import { useUpdateCustomerType } from '@/hooks/useCustomerType';
import { useDeleteCustomerAccount } from '@/hooks/useDeleteCustomerAccount';
import { useUploadFile } from '@/hooks/useFiles';
import { useUpdateSetting } from '@/hooks/useSetting';
import {
  useGetSubscription,
  useUpdateSubscription,
} from '@/hooks/useSubscription';
import { CustomerTypeOptions } from '@/sections/customerManagement/components/modals/UpdateCustomerTypeModal';
import { PeriodOfPerformance } from '@/sections/customerManagement/components/PeriodOfPerformance';
import { CustomerData } from '@/sections/customerManagement/types';
import { ScanSettingsCards } from '@/sections/settings/components';
import {
  NarrativesSection,
  PraetorianSettingsSection,
  ReportSettingsSection,
} from '@/sections/settings/sections';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { CUSTOMER_TYPE_LABELS, CustomerTypeValue } from '@/types';

interface CustomerSettingsTabProps {
  customer: CustomerData;
}

export const CustomerSettingsTab: React.FC<CustomerSettingsTabProps> = ({
  customer,
}) => {
  const { isPraetorianUser } = useAuth();
  const { drawerState } = useGlobalState();
  const [isSubscriptionTypeModalOpen, setIsSubscriptionTypeModalOpen] =
    useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState<CustomerTypeValue | null>(customer.type || null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isOrganizationEditModalOpen, setIsOrganizationEditModalOpen] =
    useState(false);
  const [editedOrganizationName, setEditedOrganizationName] = useState('');
  const updateCustomerType = useUpdateCustomerType();
  const { deleteCustomerAccount, isPending: isDeleting } =
    useDeleteCustomerAccount();

  const handleDeleteAccount = async () => {
    await deleteCustomerAccount({
      email: customer.email,
      displayName: customer.displayName,
      onSuccess: () => {
        if (drawerState.customer) {
          drawerState.setCustomer({
            ...drawerState.customer,
            type: 'DELETE',
          });
        }
        setIsDeleteModalOpen(false);
      },
    });
  };

  const { data: subscriptionData, status: subscriptionStatus } =
    useGetSubscription({
      email: customer.email,
    });
  const updateSubscription = useUpdateSubscription({ email: customer.email });

  const startDateInit = subscriptionData?.startDate || '';
  const endDateInit = subscriptionData?.endDate || '';

  const [startDate, setStartDate] = useState(startDateInit);
  const [endDate, setEndDate] = useState(endDateInit);
  const hasSubscriptionChanges =
    startDate !== startDateInit || endDate !== endDateInit;

  const handleSaveSubscriptionDates = async () => {
    if (!subscriptionData) {
      toast.error('Subscription data not loaded');
      return;
    }

    try {
      await updateSubscription.mutateAsync({
        ...subscriptionData,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
    } catch (error) {
      console.error('Failed to update subscription dates:', error);
      // The mutation already handles error toasts, so we don't need this one
    }
  };

  // Customer settings for display name
  const { data: customerSettings, refetch: refetchSettings } = useMy(
    {
      resource: 'setting',
    },
    {
      email: customer.email,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const displayNameSetting = customerSettings?.find(
    s => s.name === 'display-name'
  );
  const accountDisplayName =
    typeof displayNameSetting?.value === 'string'
      ? displayNameSetting.value
      : '';

  // Profile picture management
  const { data: profilePictureUrl, isFetching: isProfilePictureFetching } =
    useGetProfilePictureUrl(
      { email: customer.email, isOrganization: true },
      {
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always' as const,
      }
    );
  const { mutateAsync: updateSetting, isPending: isUpdatingSetting } =
    useUpdateSetting(customer.email);

  const { mutate: uploadFile, status: uploadFileStatus } = useUploadFile(
    customer.email
  );

  const isProfilePictureLoading =
    uploadFileStatus === 'pending' || isProfilePictureFetching;

  const isAccountDisplayNameLoading = isUpdatingSetting;

  const handleSaveDisplayName = async (newValue: string) => {
    await updateSetting({
      name: 'display-name',
      value: newValue,
      silent: true,
    });

    // Refetch settings to ensure UI updates immediately
    await refetchSettings();

    toast.success('Organization name updated successfully');
  };

  // Account type descriptions
  const ACCOUNT_TYPE_DESCRIPTIONS: Record<CustomerTypeValue, string> = {
    FREEMIUM: 'Free tier with limited features',
    PILOT: 'Pilot program testing features',
    ENGAGEMENT: 'Professional Services engagement',
    MANAGED: 'Full-service managed subscription',
    SAAS: 'Self-service SaaS subscription',
    DELETE: 'Marked for deletion',
  };

  return (
    <div className="text-chariot-text-primary flex flex-col space-y-6">
      {/* Praetorian Only Section */}
      <div className="flex flex-col gap-4">
        {/* Account Configuration Container */}
        <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
          <div className="p-6">
            {/* Account Settings Header */}
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-xl font-bold">Account Settings</h3>
              <Tooltip title="Define the account type and contract period for this customer">
                <InfoIcon className="text-chariot-text-secondary size-4" />
              </Tooltip>
            </div>
            {/* Organization */}
            <div className="border-chariot-stroke rounded-md border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="flex shrink-0">
                    {isProfilePictureLoading ? (
                      <div className="border-chariot-stroke flex size-16 flex-col items-center justify-center gap-3 rounded-lg border-[3px] border-dashed">
                        <Loader isLoading type="spinner" />
                      </div>
                    ) : (
                      <ProfilePictureUpload
                        source={
                          profilePictureUrl
                            ? { type: 'url', url: profilePictureUrl.url }
                            : { type: 'empty' }
                        }
                        onUpload={buffer => {
                          uploadFile({
                            name: ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
                            content: buffer,
                          });
                        }}
                        onDelete={() => {
                          uploadFile({
                            name: ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
                            content: '',
                          });
                        }}
                        size="sm"
                        shape="square"
                        disabled={!isPraetorianUser}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-chariot-text-primary text-base font-bold">
                      {accountDisplayName || 'Organization Name'}
                    </h3>
                  </div>
                </div>
                {isPraetorianUser && (
                  <Button
                    styleType="tertiary"
                    label="Edit"
                    size="sm"
                    className="h-fit"
                    onClick={() => {
                      setEditedOrganizationName(accountDisplayName || '');
                      setIsOrganizationEditModalOpen(true);
                    }}
                  />
                )}
              </div>
            </div>

            {/* Account Type - Praetorian only */}
            {isPraetorianUser && (
              <div className="border-chariot-stroke mt-3 rounded-md border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-chariot-text-primary text-base font-bold">
                      Account Type
                    </h3>
                    <p className="text-chariot-text-secondary text-sm">
                      {customer.type ? (
                        <>
                          <span className="text-chariot-text-primary font-medium">
                            {CUSTOMER_TYPE_LABELS[customer.type]}
                          </span>
                          {' - '}
                          {ACCOUNT_TYPE_DESCRIPTIONS[customer.type]}
                        </>
                      ) : (
                        'No account type configured'
                      )}
                    </p>
                  </div>
                  <Button
                    styleType="tertiary"
                    label="Edit"
                    size="sm"
                    className="h-fit"
                    onClick={() => {
                      setSelectedSubscriptionType(customer.type || null);
                      setIsSubscriptionTypeModalOpen(true);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="border-chariot-stroke mt-3 rounded-md border p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-chariot-text-primary text-base font-bold">
                      Period of Performance
                    </h3>
                    <p className="text-chariot-text-secondary text-sm">
                      {isPraetorianUser
                        ? 'Set the contract start and end dates for this customer'
                        : 'Contract start and end dates for this tenant'}
                    </p>
                  </div>
                  {hasSubscriptionChanges && (
                    <Button
                      styleType="primary"
                      label={
                        updateSubscription.isPending ? 'Saving...' : 'Save'
                      }
                      size="sm"
                      className="h-fit"
                      onClick={handleSaveSubscriptionDates}
                      disabled={updateSubscription.isPending}
                    />
                  )}
                </div>
                <Loader isLoading={subscriptionStatus === 'pending'}>
                  <PeriodOfPerformance
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={date => setStartDate(date)}
                    onEndDateChange={date => setEndDate(date)}
                    disabled={!isPraetorianUser}
                    required={false}
                    showTitle={false}
                  />
                </Loader>
              </div>
            </div>
          </div>
        </div>

        {/* Scan Settings Section */}
        <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
          <div className="p-6">
            {/* Scan Settings Header */}
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-xl font-bold">Scan Settings</h3>
              <Tooltip title="Configure scan levels, schedules, and rate limiting for this customer">
                <InfoIcon className="text-chariot-text-secondary size-4" />
              </Tooltip>
            </div>
            <ScanSettingsCards email={customer.email} editable={false} />
          </div>
        </div>

        {/* Report Settings - PlexTrac Integration */}
        {isPraetorianUser && (
          <ReportSettingsSection
            customerEmail={customer.email}
            isPraetorianUser={isPraetorianUser}
          />
        )}

        {/* Narratives - Engagement customers only */}
        {isPraetorianUser && customer.type === 'ENGAGEMENT' && (
          <NarrativesSection
            customerEmail={customer.email}
            isPraetorianUser={isPraetorianUser}
          />
        )}

        {/* Praetorian Only Section - Feature Flags & Delete Account */}
        {isPraetorianUser && (
          <PraetorianSettingsSection
            customerEmail={customer.email}
            customerDisplayName={customer.displayName}
            isPraetorianUser={isPraetorianUser}
            showDeleteAccount={true}
            onDeleteAccount={() => setIsDeleteModalOpen(true)}
          />
        )}

        <Modal
          title="Update Subscription Type"
          subtitle={`Select a subscription type for ${customer.displayName || customer.email}`}
          open={isSubscriptionTypeModalOpen}
          onClose={() => {
            setIsSubscriptionTypeModalOpen(false);
            setSelectedSubscriptionType(customer.type || null);
          }}
          footer={{
            text: updateCustomerType.isPending ? 'Updating...' : 'Save',
            onClick: async () => {
              if (!selectedSubscriptionType) {
                toast.error('Please select a subscription type');
                return;
              }
              try {
                await updateCustomerType.mutateAsync({
                  email: customer.email,
                  customerType: selectedSubscriptionType,
                });

                // Only update the type in the global state, not replace the entire customer object
                // This prevents loss of other customer data
                if (drawerState.customer) {
                  drawerState.setCustomer({
                    ...drawerState.customer,
                    type: selectedSubscriptionType,
                  });
                }

                toast.success(`Subscription type updated successfully`);
                setIsSubscriptionTypeModalOpen(false);
              } catch {
                toast.error('Failed to update subscription type');
              }
            },
            disabled: !selectedSubscriptionType || updateCustomerType.isPending,
            styleType: 'primary',
          }}
        >
          <CustomerTypeOptions
            selectedType={selectedSubscriptionType}
            setSelectedType={setSelectedSubscriptionType}
            isPending={updateCustomerType.isPending}
          />
        </Modal>

        <Modal
          title="Edit Organization Name"
          subtitle={`Update the organization name for ${customer.displayName || customer.email}`}
          open={isOrganizationEditModalOpen}
          onClose={() => {
            setIsOrganizationEditModalOpen(false);
            setEditedOrganizationName('');
          }}
          footer={{
            text: isAccountDisplayNameLoading ? 'Saving...' : 'Save',
            onClick: async () => {
              if (!editedOrganizationName.trim()) {
                toast.error('Organization name cannot be empty');
                return;
              }
              try {
                await handleSaveDisplayName(editedOrganizationName);
                setIsOrganizationEditModalOpen(false);
                setEditedOrganizationName('');
              } catch {
                toast.error('Failed to update organization name');
              }
            },
            disabled:
              !editedOrganizationName.trim() || isAccountDisplayNameLoading,
            styleType: 'primary',
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-chariot-text-primary text-sm font-medium">
                Organization Name
              </label>
              <input
                type="text"
                value={editedOrganizationName}
                onChange={e => setEditedOrganizationName(e.target.value)}
                placeholder="Enter organization name"
                className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary focus:ring-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              />
            </div>
          </div>
        </Modal>
      </div>

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        accountName={customer.displayName || customer.email}
        isPending={isDeleting}
      />
    </div>
  );
};
